<template>
    <div>
        <div class="flex items-center px-2 py-3 sm:px-4">
            <div class="flex min-w-0 flex-1 items-center">
                <div class="flex-shrink-0">
                    <div class="flex items-center justify-center rounded-full border-2 border-gray-300 bg-white p-3">
                        <CreditCardIcon class="border-gray-150 w-6" />
                    </div>
                </div>
                <div class="min-w-0 flex-1 items-center px-4 md:grid md:grid-cols-4 md:gap-4">
                    <div>
                        <p class="text-sm font-medium text-gray-900">
                            <span class="truncate">{{ bankAccount.holder }}</span>
                        </p>
                        <p class="mt-2 flex items-center text-sm text-gray-500">
                            <span class="break-all">{{ bankAccount.iban }}</span>
                        </p>
                    </div>
                    <div>
                        <BankAccountStatusIndicator :status="bankAccount.status" />
                        <span v-if="isPrimaryForCurrentPlatform" class="mt-1 flex items-center text-sm text-blue-800">
                            <StarIcon class="mr-1 w-4" />
                            {{ $t('bank_account.primary_bank_account') }}
                        </span>
                    </div>
                    <div class="flex justify-end md:col-span-2">
                        <a
                            v-if="bankAccount.status === BankAccountStatus.VERIFIED && !isPrimaryForCurrentPlatform"
                            class="mr-4 cursor-pointer self-center text-sm text-gray-900"
                            @click="setAsPrimary()">
                            {{ $t('bank_account.set_as_main_bank_account') }}
                        </a>
                        <BaseButtonMuted class="mr-2 self-end" @click="edit()">
                            <PencilIcon class="w-4" />
                        </BaseButtonMuted>
                        <BaseButtonMuted
                            :disabled="removing || isPrimaryForCurrentPlatform"
                            class="self-end"
                            @click="remove()">
                            <MinusCircleIcon class="w-4 text-red-500"></MinusCircleIcon>
                        </BaseButtonMuted>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="editing" class="px-4 pb-4 sm:px-4">
            <AccountAddEditBankAccountForm
                :profile-uid="profileUid"
                :profile-type="profileType"
                :bank-account="bankAccount"
                :existing-bank-accounts="existingBankAccounts"
                @saved="onSaved"
                @cancel="editing = false" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { CreditCardIcon, PencilIcon, StarIcon } from '@heroicons/vue/solid'
import { MinusCircleIcon } from '@heroicons/vue/outline'
import { computed, ref } from 'vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import BankAccountStatusIndicator from '../statusIndicator/BankAccountStatusIndicator.vue'
import AccountAddEditBankAccountForm from './AccountAddEditBankAccountForm.vue'
import { BankAccountStatus } from '../../../../typings'
import { deleteBankAccount, FrontendBankAccountResource, putBankAccountUpdate } from '../../../../utilities/api'
import { notifySuccess } from '../../../../utilities/useNotifications'
import { openConfirmDialog } from '../../../../utilities/useDialog'
import { getPlatformName } from '../../../../utilities/usePlatformSettings'
import { i18n } from '../../../../localization'

const props = defineProps<{
    bankAccount: FrontendBankAccountResource
    existingBankAccounts: FrontendBankAccountResource[]
    profileUid: string
    profileType: 'natural' | 'legal'
}>()

const emit = defineEmits(['updated'])

const removing = ref(false)
const editing = ref(false)

const isPrimaryForCurrentPlatform = computed(() => {
    return (props.bankAccount.primary_for_platforms || []).includes(getPlatformName())
})

function edit() {
    editing.value = true
}

function onSaved() {
    editing.value = false
    emit('updated')
}

async function setAsPrimary() {
    try {
        await putBankAccountUpdate(props.bankAccount.id, { is_primary: true })
        notifySuccess(i18n.global.t('bank_account.set_as_main_bank_account_success_message'))
        emit('updated')
    } catch (error) {
        // The api client's response interceptor already shows a notification with the actual
        // validation/business-rule message for 422/400/403 errors - nothing to add here.
    }
}

async function remove() {
    const confirmed = await openConfirmDialog(
        i18n.global.t('bank_account.delete_confirm_title'),
        i18n.global.t('bank_account.delete_confirm_message')
    ).catch(() => false)

    if (!confirmed) {
        return
    }

    removing.value = true
    try {
        await deleteBankAccount(props.bankAccount.id)
        notifySuccess(i18n.global.t('bank_account.delete_success_message'))
        emit('updated')
    } catch (error) {
        // The api client's response interceptor already shows a notification with the actual
        // validation/business-rule message for 422/400/403 errors - nothing to add here.
    } finally {
        removing.value = false
    }
}
</script>

<style scoped></style>
